import { Box, Button, Divider, List, Stack } from '@mui/material';

import ErrorMessage from '@/components/errorMessage';
import { useEffect, useRef, useState } from 'react';
import SourceEntity from './sourceEntity';

type Props = {
  entity: any;
  maxHeight?: number;
  sourceEntities: any[];
  onAddSource: (source: any) => void;
  onDeleteSource: (source: any) => void;
};

const SourceEntities = ({ entity = {}, maxHeight, sourceEntities, onAddSource, onDeleteSource }: Props) => {
  const newSourceRef = useRef<null | HTMLDivElement>();

  const [newSource, setNewSource] = useState(false);
  const [newSourceError, setNewSourceError] = useState('');
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (newSourceRef.current && newSource) {
      newSourceRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [newSourceRef.current, newSource]);

  const addEvent = () => {
    setNewSourceError('');
    setNewSource(true);
  };

  const addSource = (source: any) => {
    const es = (entity.sourceEntities || []).find(
      (es: any) =>
        es.sourceEntityDefId === source.sourceEntityDefId &&
        es.sourceEntityType === source.sourceEntityType &&
        es.sourceEntityId === source.sourceEntityId,
    );
    if (!es) {
      onAddSource(source);
      setNewSource(false);
      setNewSourceError('');
    } else {
      setNewSourceError('Already exists');
    }
  };

  const deleteSource = (eventSubscription: any) => onDeleteSource(eventSubscription);
  const cancelAdd = () => {
    setNewSourceError('');
    setNewSource(false);
  };

  return (
    <Box sx={{ height: maxHeight ? `${maxHeight}px` : '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction={'row'} spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => addEvent()}
          disabled={newSource}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Add new source
        </Button>
      </Stack>
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <List dense>
          {(entity.sourceEntities || []).map((source: any, idx: any) => (
            <Box key={idx}>
              <SourceEntity
                entity={entity}
                sourceEntity={source}
                sourceEntities={sourceEntities}
                onAdd={addSource}
                onDelete={deleteSource}
                onCancelAdd={cancelAdd}
              />
              <Divider variant="fullWidth" component="li" />
            </Box>
          ))}
        </List>
        {newSource && (
          <SourceEntity
            entity={entity}
            sourceEntities={sourceEntities}
            onAdd={addSource}
            onDelete={deleteSource}
            onCancelAdd={cancelAdd}
            errror={newSourceError}
          />
        )}
        {newSourceError && <ErrorMessage error={{ message: newSourceError }} />}
      </Box>
    </Box>
  );
};

export default SourceEntities;
